import { useState, useEffect, useCallback, RefObject, useRef } from "react";
import { toast } from "react-toastify";
import {
  getRelatedProperties,
  generateGraphviz,
  EnrichedRuleTable,
} from "./helpers";
import { Ruleset } from "store/reducers/products/parameters/pl-parameters.types";

export type DialogSizeType = "md" | "lg" | "xl";

/**
 * Hook to manage dialog size, persisting the preference in localStorage.
 */
export const useDialogSize = (initialSize: DialogSizeType = "lg") => {
  const availableSizes: DialogSizeType[] = ["md", "lg", "xl"];
  const getInitialSize = (): DialogSizeType => {
    const preferred = localStorage.getItem(
      "preferedDialogSize"
    ) as DialogSizeType;
    return availableSizes.includes(preferred) ? preferred : initialSize;
  };

  const [dialogSize, setDialogSize] = useState<DialogSizeType>(getInitialSize);

  const handleSizeToggle = useCallback(() => {
    setDialogSize((prevSize) => {
      const currentIndex = availableSizes.indexOf(prevSize);
      const nextIndex = (currentIndex + 1) % availableSizes.length;
      const nextSize = availableSizes[nextIndex];
      localStorage.setItem("preferedDialogSize", nextSize);
      return nextSize;
    });
  }, []);

  return { dialogSize, handleSizeToggle };
};

/**
 * Hook to fetch and filter graph data based on rulesets.
 */
export const useGraphData = (
  open: boolean,
  productLineId: string,
  propertyId: string,
  initialRuleSets: Ruleset[]
) => {
  const [isLoading, setIsLoading] = useState(true);
  const [allTables, setAllTables] = useState<any[]>([]);
  const [filteredTables, setFilteredTables] = useState<any[]>([]);
  const [selectedRulesets, setSelectedRulesets] = useState<string[]>([]);

  useEffect(() => {
    if (!open) {
      setAllTables([]);
      setFilteredTables([]);
      setSelectedRulesets([]);
      return;
    }

    setIsLoading(true);
    setSelectedRulesets(initialRuleSets.map((rs) => rs.id));

    if (propertyId) {
      getRelatedProperties(productLineId, propertyId)
        .then((tablesResponse) => {
          setAllTables(tablesResponse.ruleTables);
          setFilteredTables(tablesResponse.ruleTables);
        })
        .catch((e) => {
          console.error(e);
          toast.error("Failed to load related properties. Please try again.");
        });
    }
  }, [open, productLineId, propertyId, initialRuleSets]);

  useEffect(() => {
    if (allTables.length === 0 && open) return;

    const newFilteredTables = allTables.filter((table: any) => {
      const tableRulesetIds = table.rulesets.map((ruleset: any) => ruleset.id);
      return selectedRulesets.some((selectedId: string) =>
        tableRulesetIds.includes(selectedId)
      );
    });
    setFilteredTables(newFilteredTables);
  }, [selectedRulesets, allTables, open]);

  return {
    isLoading,
    setIsLoading,
    filteredTables,
    selectedRulesets,
    setSelectedRulesets,
  };
};

/**
 * Hook to generate Graphviz dot content from filtered data.
 */
export const useGraphvizDot = (
  filteredTables: EnrichedRuleTable[],
  propertyName: string,
  subpropertyFilter: boolean,
  pvaFilter: boolean,
  setIsLoading: (loading: boolean) => void
) => {
  const [dotContent, setDotContent] = useState("");

  useEffect(() => {
    if (!pvaFilter) {
      filteredTables = filteredTables.filter((table) => {
        return table.tableType !== "ATTR_LOOKUP";
      });
    }
    const graph = generateGraphviz(
      filteredTables,
      propertyName,
      subpropertyFilter
    );
    const sizeInKB = (graph.length * 2) / 1024;
    if (sizeInKB > 68) {
      setDotContent(
        'digraph G { node [shape=box]; "Graph too large to be rendered" [style=filled, fillcolor=red]; }'
      );
    } else {
      setDotContent(graph);
    }
    setIsLoading(false);
  }, [
    filteredTables,
    propertyName,
    subpropertyFilter,
    setIsLoading,
    pvaFilter,
  ]);

  return dotContent;
};

/**
 * Hook to manage edge click interactions on the graph.
 */
export const useGraphEdgeInteraction = (
  graphvizRef: RefObject<HTMLDivElement>,
  isLoading: boolean,
  dotContent: string,
  handleEdgeClick: (edgeId: string) => void,
  showEdgePopup: boolean
) => {
  const listenersRef = useRef<{ element: Element; handler: () => void }[]>([]);

  useEffect(() => {
    const graphContainer = graphvizRef.current;
    if (isLoading || !graphContainer || !dotContent) return;

    // Clear any existing listeners before adding new ones
    listenersRef.current.forEach(({ element, handler }) =>
      element.removeEventListener("click", handler)
    );
    // Reset the ref to only store the new listeners
    listenersRef.current = [];
    const edges = graphContainer.querySelectorAll(".edge");

    edges.forEach((edge) => {
      const edgeId = edge.id || edge.getAttribute("data-edge-id") || "unknown";
      const clickHandler = () => handleEdgeClick(edgeId);
      edge.addEventListener("click", clickHandler);
      (edge as HTMLElement).style.cursor = "pointer";
      edge.querySelectorAll("*").forEach((child) => {
        (child as HTMLElement).style.cursor = "pointer";
      });
      listenersRef.current.push({ element: edge, handler: clickHandler });
    });

    return () =>
      listenersRef.current.forEach(({ element, handler }) =>
        element.removeEventListener("click", handler)
      );
  }, [isLoading, dotContent, graphvizRef, handleEdgeClick, showEdgePopup]);
};
